import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import React, { useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { CheckBox } from "@whoosh/design-system/src/components/controls";
import {
  Box,
  HStack,
  Text,
  VStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import { selectedCoursesByFacility } from "../../../atoms/selectedCoursesByFacility";
import {
  MultiSlotCourseFiltersQuery,
  MultiSlotCourseFiltersQuery$data,
} from "./__generated__/MultiSlotCourseFiltersQuery.graphql";
import { buildCourseCheckedState } from "./courseSelection";

type MultiSlotCourseFiltersProps = {
  queryRef: PreloadedQuery<MultiSlotCourseFiltersQuery>;
  currentCheckedCourses: Record<string, boolean> | null;
  setCurrentCheckedCourses: (courses: Record<string, boolean>) => void;
  isModalView?: boolean;
};

type NodeCourses = NonNullable<
  MultiSlotCourseFiltersQuery$data["node"]
>["courses"];
type Edges = NonNullable<NodeCourses>["edges"];
type Courses = Edges | null;

type Course = {
  name: string;
  id: string;
  bucket: string;
};

type Accumulator = Course[][];

export const MultiSlotCourseFilters = ({
  queryRef,
  isModalView,
  currentCheckedCourses,
  setCurrentCheckedCourses,
}: MultiSlotCourseFiltersProps) => {
  const data = usePreloadedQuery(MultiSlotCourseFiltersQueryString, queryRef);
  const facilityId = data?.node?.id;
  const courses: Courses = data?.node?.courses?.edges ?? null;
  const initialValue: Accumulator = [];
  const bucketedCourses = courses
    ? courses.reduce((acc: Accumulator, course) => {
        if (!course || !course.node) return acc;
        const key: any = course.node.courseBucket?.id ?? "none";
        if (!acc[key]) {
          acc[key] = [];
        }
        const c = {
          id: course.node.id,
          name: course.node.name,
          bucket: course.node.courseBucket?.name ?? "none",
        };
        acc[key].push(c);
        return acc;
      }, initialValue)
    : null;

  return (
    <Box>
      <Text variant="mainHeading" color="grayscale-1000" pb={6}>
        {t`Filters`}
      </Text>
      {facilityId ? (
        <InnerContent
          bucketedCourses={bucketedCourses}
          facilityId={facilityId}
          isModalView={!!isModalView}
          currentCheckedCourses={currentCheckedCourses}
          setCurrentCheckedCourses={setCurrentCheckedCourses}
        />
      ) : null}
    </Box>
  );
};

const InnerContent = ({
  bucketedCourses,
  isModalView,
  currentCheckedCourses,
  setCurrentCheckedCourses,
  facilityId,
}: {
  bucketedCourses: Accumulator | null;
  facilityId: string;
  isModalView: boolean;
  currentCheckedCourses: Record<string, boolean> | null;
  setCurrentCheckedCourses: (courses: Record<string, boolean>) => void;
}) => {
  const [checkedCourses, setCheckedCourses] = useAtom(
    selectedCoursesByFacility(facilityId)
  );

  useEffect(() => {
    if (bucketedCourses !== null && !checkedCourses && bucketedCourses) {
      const initialCheckedCourses = buildCourseCheckedState(
        bucketedCourses,
        true
      );
      if (isModalView) {
        setCurrentCheckedCourses(initialCheckedCourses);
      } else {
        setCheckedCourses(initialCheckedCourses);
      }
    }
  }, [
    bucketedCourses,
    checkedCourses,
    setCheckedCourses,
    setCurrentCheckedCourses,
    isModalView,
  ]);

  const areAllCoursesChecked: boolean = isModalView
    ? bucketedCourses && currentCheckedCourses
      ? Object.values(bucketedCourses)
          .flat()
          .every((course) => currentCheckedCourses[course.id])
      : false
    : bucketedCourses && checkedCourses
    ? Object.values(bucketedCourses)
        .flat()
        .every((course) => checkedCourses[course.id])
    : false;

  const toggleAllCourses = () => {
    const newCheckedState = bucketedCourses
      ? buildCourseCheckedState(bucketedCourses, true)
      : {};

    if (isModalView) {
      setCurrentCheckedCourses(newCheckedState);
    } else {
      setCheckedCourses(newCheckedState);
    }
  };

  const handleCheckboxChange = (courseId: string) => {
    if (isModalView) {
      setCurrentCheckedCourses({
        ...currentCheckedCourses,
        [courseId]:
          currentCheckedCourses && currentCheckedCourses[courseId]
            ? !currentCheckedCourses[courseId]
            : true,
      });
    } else {
      setCheckedCourses({
        ...checkedCourses,
        [courseId]:
          checkedCourses && checkedCourses[courseId]
            ? !checkedCourses[courseId]
            : true,
      });
    }
  };

  const areAllCoursesInBucketChecked = (courses: Course[]) => {
    if (isModalView) {
      return currentCheckedCourses
        ? courses.every((course) => currentCheckedCourses[course.id])
        : false;
    } else {
      return checkedCourses
        ? courses.every((course) => checkedCourses[course.id])
        : false;
    }
  };

  const toggleCoursesInBucket = (courses: Course[]) => {
    const areAllChecked = areAllCoursesInBucketChecked(courses);
    const newCheckedState = isModalView
      ? { ...currentCheckedCourses }
      : { ...checkedCourses };

    courses.forEach((course) => {
      newCheckedState[course.id] = !areAllChecked;
    });

    if (isModalView) {
      setCurrentCheckedCourses(newCheckedState);
    } else {
      setCheckedCourses(newCheckedState);
    }
  };

  return bucketedCourses ? (
    <VStack gap={4}>
      <HStack key="select-all-selector" gap={4} alignItems={"center"}>
        <CheckBox
          isChecked={areAllCoursesChecked}
          onPress={toggleAllCourses}
          label={t`Select All`}
        />
      </HStack>

      {Object.entries(bucketedCourses).map(([key, courses]) => (
        <React.Fragment key={key}>
          {key !== "none" ? (
            <HStack
              key={`select-all-${key}`}
              gap={1}
              pt={4}
              pb={2}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Text
                variant="mainSmallSemiBold"
                fontWeight={"600"}
                color="grayscale-1000"
              >
                {courses[0].bucket}
              </Text>
              <Pressable
                onPress={() => toggleCoursesInBucket(courses)}
                maxHeight={20}
              >
                <Text
                  variant="mainSmallSemiBold"
                  fontWeight={"600"}
                  color="main-primary-500"
                >
                  {areAllCoursesInBucketChecked(courses)
                    ? t`Deselect All`
                    : t`Select All`}
                </Text>
              </Pressable>
            </HStack>
          ) : (
            <Box pb={4} />
          )}
          {courses.map((course) => (
            <HStack key={course.id} gap={4} alignItems={"center"}>
              <CheckBox
                key={`checkbox-${course.id}`}
                isChecked={
                  isModalView
                    ? currentCheckedCourses && currentCheckedCourses[course.id]
                      ? currentCheckedCourses[course.id]
                      : false
                    : checkedCourses && checkedCourses[course.id]
                    ? checkedCourses[course.id]
                    : false
                }
                onPress={() => handleCheckboxChange(course.id)}
                label={course.name}
              />
            </HStack>
          ))}
        </React.Fragment>
      ))}
    </VStack>
  ) : null;
};

export const MultiSlotCourseFiltersQueryString = graphql`
  query MultiSlotCourseFiltersQuery($facilityId: ID!) {
    node(id: $facilityId) {
      id
      ... on Facility {
        courses(first: 100, archived: false) {
          edges {
            node {
              id
              name
              courseBucket {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;
